import axios from 'axios'
import jwtDecode from 'jwt-decode'
import { encodedURL } from 'services/url'
import { XWWWFORMURLENCODED } from 'constants/request'
import { TOKEN_NAME } from 'constants/token'
import SettingsSingleton from './SettingsSingleton'
import { API_URL_SSO_LOGOUT } from '../constants/api'
import { SSO_RETURN_URL_KEY } from '../constants/sso'

function toFormData(data) {
  return Object.entries(data)
    .reduce(
      (formData, entry) => {
        formData.append(entry[0], entry[1])
        return formData
      },
      new URLSearchParams(),
    )
}

class QueryClient {
  locale

  controller

  headers = {
    'Content-Type': 'application/json',
  }

  constructor(locale) {
    this.locale = locale
    this.request = this.request.bind(this)
    this.get = this.get.bind(this)
    this.post = this.post.bind(this)
    this.patch = this.patch.bind(this)
    this.put = this.put.bind(this)
    this.delete = this.delete.bind(this)
    this.updateHeaders()
    this.#setupInterceptors()
    this.controller = new AbortController()
  }

  /**
   * localStorage is the unique source of truth here, like a store
   */
  updateHeaders() {
    const token = window.localStorage.getItem(TOKEN_NAME)
    const locale = this.#getDefaultLocale()
    this.headers = {
      ...this.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Accept-Language': locale,
    }
  }

  #getDefaultLocale() {
    return window.localStorage.getItem('locale') || this.locale
  }

  #setupInterceptors() {
    axios.interceptors.request.use(
      config => {
        const token = window.localStorage.getItem(TOKEN_NAME)
        if (token) {
          try {
            const decoded = jwtDecode(token)
            const currentTime = Math.floor(Date.now() / 1000)
            if (decoded.exp <= currentTime) {
              this.#handleTokenExpiration()

              throw new axios.Cancel('Token expiré')
            }
          } catch (error) {
            this.#handleTokenExpiration()

            throw new axios.Cancel('Token invalide')
          }
        }

        return {
          ...config,
          params: {
            ...config.params,
            _locale: window.localStorage.getItem('locale'),
          },
        }
      },
      error => { throw error },
    )
  }

  /**
   * Manage invalid token
   */
  #handleTokenExpiration() {
    const from = encodedURL()
    if (typeof window !== 'undefined') {
      // Remember the current route so that, after SSO, we can redirect the user back to it.
      // The query client is the single point handling token expiration for XHR calls:
      // if an API request notices the token is stale, we capture the URL here before
      // triggering the logout + SSO handshake.
      const { pathname, search, hash } = window.location
      const isSsoRoute = pathname === '/sso'
      const lowerPath = pathname?.toLowerCase?.() || ''
      const isAuthRoute = lowerPath.includes('/login') || lowerPath.includes('/reset-password')
      if (!isSsoRoute && !isAuthRoute) {
        const intendedUrl = `${pathname}${search}${hash}`
        if (intendedUrl) {
          window.sessionStorage.setItem(SSO_RETURN_URL_KEY, intendedUrl)
        }
      }
    }
    window.localStorage.removeItem(TOKEN_NAME)
    const settings = SettingsSingleton.getInstance().getSettings(this)
    if (settings?.ssoEnabled) {
      window.location.replace(API_URL_SSO_LOGOUT)
    }
    window.location.href = `/${this.locale}/login?from=${from}`
  }

  /**
   * Basic request
   */
  async request(url, options, headers) {
    const { method, data, plainResponse, responseType } = options || {
      method: 'get',
      data: {},
      plainResponse: false,
      responseType: 'json',
    }
    const resp = await axios.request({
      url,
      headers: headers || this.headers,
      method,
      data,
      responseType,
      ...options,
      signal: this.controller.signal,
    })
    return plainResponse ? resp : resp?.data
  }

  /**
   * GET
   */
  async get(url, config = {}, key = null) {
    if (key) this.requests[key] = url

    const resp = await axios.request(url, {
      ...config,
      headers: this.headers,
      method: 'get',
      signal: this.controller.signal,
    })

    return (resp.data?.['hydra:member'] ?? false)
      ? { items: resp.data['hydra:member'], count: resp.data['hydra:totalItems'] ?? undefined }
      : resp.data
  }

  /**
   * POST
   */
  async post(url, data, config) {
    const isFormData = config && config.headers && config.headers['Content-Type'] === XWWWFORMURLENCODED
    const payload = isFormData ? toFormData(data) : data

    const { headers: extraHeaders, ...rest } = config || { headers: {} }

    const resp = await axios.request(url, {
      data: payload,
      method: 'post',
      headers: {
        ...this.headers,
        ...extraHeaders,
      },
      ...rest,
    })

    return resp.data
  }

  /**
   * PATCH
   */
  async patch(url, data, options = {}) {
    const resp = await axios.request(url, {
      data,
      headers: {
        ...this.headers,
        'Content-Type': 'application/merge-patch+json',
      },
      method: 'PATCH',
    })
    return options.plainResponse ? resp : resp.data
  }

  /**
   * PUT
   */
  async put(url, data) {
    const resp = await axios.request(url, {
      data,
      headers: this.headers,
      method: 'PUT',
    })
    return resp.data
  }

  /**
   * DELETE
   */
  async delete(url) {
    const resp = await axios.request(url, {
      headers: this.headers,
      method: 'DELETE',
    })
    return resp.data
  }
}

export default QueryClient
